
import React from 'react';
import { isConfigured } from '../services/firebaseService';

interface SettingsPageProps {
  syncStatus: 'CLOUD' | 'LOCAL';
  darkMode: boolean;
  setDarkMode: (val: boolean) => void;
  onResetCache: () => void;
  onClearStandards: () => void;
  onForceSync: () => Promise<void>;
}

const SettingsPage: React.FC<SettingsPageProps> = ({ syncStatus, darkMode, setDarkMode, onResetCache, onClearStandards, onForceSync }) => {
  const isCloudSetup = isConfigured();
  const hasPermissionError = syncStatus === 'LOCAL' && isCloudSetup;

  return (
    <div className="max-w-4xl mx-auto space-y-12 animate-in fade-in duration-500">
       <div className="flex justify-between items-end border-b border-slate-200 dark:border-slate-700 pb-8">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">Settings</h2>
       </div>

       {hasPermissionError && (
          <div className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-200 border border-red-200 dark:border-red-800 p-6 rounded-xl shadow-sm">
             <h3 className="text-lg font-bold mb-2 flex items-center gap-2">
               <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
               Permission Denied
             </h3>
             <p className="text-sm mb-4 leading-relaxed">
                Database Firebase Anda menolak akses. Ikuti langkah ini untuk memperbaiki Rules di Firebase Console:
             </p>
             <div className="space-y-4">
                <div className="bg-white/50 dark:bg-black/30 p-4 rounded-lg border border-red-100 dark:border-red-800/50">
                   <p className="text-xs font-mono mb-1">1. Buka Firebase Console &gt; Firestore Database &gt; Rules.</p>
                   <p className="text-xs font-mono">2. Ganti kode rules dengan yang di bawah ini:</p>
                </div>
                
                <div className="bg-slate-900 text-slate-300 p-4 rounded-lg font-mono text-xs overflow-x-auto relative group border border-slate-800">
                   <button 
                      onClick={() => navigator.clipboard.writeText("rules_version = '2';\nservice cloud.firestore {\n  match /databases/{database}/documents {\n    match /{document=**} {\n      allow read, write: if true;\n    }\n  }\n}")}
                      className="absolute right-3 top-3 bg-white/10 hover:bg-white/20 px-2 py-1 text-white rounded text-[10px] transition-colors"
                   >
                      COPY
                   </button>
                   <pre>{`rules_version = '2';
service cloud.firestore {
  match /databases/{database}/documents {
    match /{document=**} {
      allow read, write: if true;
    }
  }
}`}</pre>
                </div>
                <p className="text-xs italic opacity-80">
                   *Catatan: Rule ini mengizinkan akses publik. Untuk produksi masal, gunakan sistem login (Firebase Auth).
                </p>
                <button 
                   onClick={() => window.location.reload()}
                   className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors shadow-sm"
                >
                   Coba Re-Sync Sekarang
                </button>
             </div>
          </div>
       )}

       {!isCloudSetup && (
          <div className="bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-200 border border-blue-200 dark:border-blue-800 p-6 rounded-xl shadow-sm">
             <h3 className="text-lg font-bold mb-2 flex items-center gap-2">
               <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
               Setup Cloud Database
             </h3>
             <p className="text-sm mb-4 leading-relaxed">
                Anda saat ini berada di mode <span className="bg-white dark:bg-blue-900 px-1.5 py-0.5 rounded text-xs font-bold border border-blue-200 dark:border-blue-700">LOCAL ONLY</span>. 
                Data Anda tersimpan di browser ini saja.
             </p>
             <div className="space-y-2 font-mono text-xs bg-white/50 dark:bg-black/30 p-4 rounded-lg border border-blue-100 dark:border-blue-800/50">
                <p>1. Buka file: <span className="font-bold">services/firebaseService.ts</span></p>
                <p>2. Ganti <span className="font-bold">firebaseConfig</span> dengan nilai dari Console.</p>
             </div>
          </div>
       )}

       <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Persistence Panel */}
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-6 rounded-xl shadow-sm">
             <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-6 pb-2 border-b border-slate-100 dark:border-slate-700">Cloud Configuration</h3>
             <div className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg border border-slate-100 dark:border-slate-700">
                   <span className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Connection Engine</span>
                   <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${syncStatus === 'CLOUD' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'}`}>
                      {syncStatus === 'CLOUD' ? 'FIRESTORE ONLINE' : 'LOCAL PERSISTENCE'}
                   </span>
                </div>
                <button 
                  onClick={onForceSync}
                  className="w-full py-2.5 bg-slate-900 text-white dark:bg-white dark:text-slate-900 rounded-lg text-sm font-medium hover:bg-slate-800 dark:hover:bg-slate-100 transition-colors shadow-sm"
                >
                  Force Cloud Re-Sync
                </button>
             </div>
          </div>

          {/* System Control Panel */}
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-6 rounded-xl shadow-sm">
             <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-6 pb-2 border-b border-slate-100 dark:border-slate-700">System Controls</h3>
             <div className="space-y-6">
                <div className="flex items-center justify-between gap-4">
                   <span className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Environment Theme</span>
                   <button 
                      onClick={() => setDarkMode(!darkMode)}
                      className="px-4 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-xs font-medium bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
                   >
                      {darkMode ? 'Switch to Light' : 'Switch to Dark'}
                   </button>
                </div>
                <div className="flex items-center justify-between gap-4">
                   <span className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Technical Cache</span>
                   <div className="flex gap-2">
                     <button 
                        onClick={onClearStandards}
                        className="px-4 py-2 bg-orange-50 text-orange-600 border border-orange-200 rounded-lg text-xs font-medium hover:bg-orange-100 transition-colors dark:bg-orange-900/20 dark:border-orange-900 dark:text-orange-400 dark:hover:bg-orange-900/40"
                     >
                        Clear Standards
                     </button>
                     <button 
                        onClick={onResetCache}
                        className="px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg text-xs font-medium hover:bg-red-100 transition-colors dark:bg-red-900/20 dark:border-red-900 dark:text-red-400 dark:hover:bg-red-900/40"
                     >
                        Purge All Data
                     </button>
                   </div>
                </div>
             </div>
          </div>
       </div>
    </div>
  );
};

export default SettingsPage;
